import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import authApiService from "../../services/auth-api.service";
import operatorApiService from "../../services/operator-api.service";
import { LobbyInitData, LobbyRefreshData } from "../../types/lobby";
import { initMqttClient } from "../../utils/mqtt-client";

import Header from "../../components/header/Header";
import CrStatusList from "./components/crStatus/CrStatusList";
import VehicleStatusList from "./components/vehicleStatus/VehicleStatusList";
import Map from "./components/map/Map";

import "./Lobby.scss";

const Lobby = () => {
  const navigate = useNavigate();

  const [initData, setInitData] = useState<LobbyInitData | null>(null);
  const [refreshDataMap, setRefreshDataMap] = useState<
    Record<string, LobbyRefreshData>
  >({});
  const [footerLabel, setFooterLabel] = useState("監視中車両: 未選択");
  const [userName, setUserName] = useState("");
  const [baseId, setBaseId] = useState<number | null>(null);
  const [baseName, setBaseName] = useState("");
  const [roleId, setRoleId] = useState<number | null>(null);
  const [monitorConsoleId, setMonitorConsoleId] = useState<number | null>(null);
  const [isMqttConnected, setIsMqttConnected] = useState<boolean>(true);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await authApiService.me();

        if (user.role_cd !== "03") {
          navigate("/login");
          return;
        }

        setUserName(user.user_name);
        setBaseId(user.base_id);
        setBaseName(user.base_name);
        setRoleId(user.role_id);
        setMonitorConsoleId(user.monitor_console_id);
      } catch (error) {
        navigate("/login");
      }
    };

    fetchMe();
  }, [navigate]);

  useEffect(() => {
    if (baseId === null || roleId === null) return;

    const fetchData = async () => {
      try {
        const response = await operatorApiService.doGetInitDataForLobby({
          monitorConsoleId,
        });
        setInitData(response);
      } catch (error) {
        console.error("初期データ取得エラー:", error);
      }
    };

    fetchData();
  }, [monitorConsoleId]);

  useEffect(() => {
    const mqttRoleId = import.meta.env.VITE_MQTT_LOBBY_ROLE_ID;
    const mqttClient = initMqttClient(mqttRoleId, {
      onError: () => setIsMqttConnected(false),
    });
    return () => {
      mqttClient.end(true);
    };
  }, [initData]);

  useEffect(() => {
    if (!initData?.vehicles?.length) return;

    const fetchAllRefreshData = async () => {
      const vehicleCommunicationCodes = initData.vehicles.map(
        (v) => v.vehicle_communication_code
      );

      try {
        const responseDataMap: Record<string, LobbyRefreshData> =
          await operatorApiService.doGetAllRefreshDataForLobby({
            vehicleCommunicationCodes,
            baseId,
            monitorConsoleId,
          });

        setRefreshDataMap(responseDataMap);
      } catch (err) {
        console.error("リフレッシュデータ取得エラー", err);
      }
    };

    fetchAllRefreshData();
    const interval = setInterval(fetchAllRefreshData, 1000);
    return () => clearInterval(interval);
  }, [initData]);

  useEffect(() => {
    const monitoredVehicles = Object.values(refreshDataMap).flatMap((data) => {
      if (Array.isArray(data.selected_monitor)) {
        return data.selected_monitor.map((m) => {
          const consoleNo = m.monitor_console_no ?? "";
          const label = m.vehicle_label ?? "";
          return `監視${consoleNo}: ${label}`;
        });
      }
      return [];
    });

    if (monitoredVehicles.length > 0) {
      setFooterLabel(monitoredVehicles.join("　"));
    } else {
      setFooterLabel("監視車両: 未選択");
    }
  }, [refreshDataMap]);

  return (
    <>
      <div className="lobbyScreen">
        <Header userName={userName} baseName={baseName} />

        <div className="mainContent">
          <div className="leftArea">
            <CrStatusList refreshDataMap={refreshDataMap} />
          </div>

          <div className="rightContentArea">
            <div className="centerRow">
              <div className="middleArea">
                <div className="middleTitle">車両状況</div>
                <VehicleStatusList
                  initData={initData}
                  refreshDataMap={refreshDataMap}
                  isMqttConnected={isMqttConnected}
                />
              </div>
              <div className="rightArea">
                <div className="rightTitle">マップ</div>
                <Map
                  baseId={baseId}
                  baseName={baseName}
                  vehicles={initData?.vehicles ?? []}
                  refreshDataMap={refreshDataMap}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="footer">
          <div>{footerLabel}</div>
        </div>
      </div>
    </>
  );
};

export default Lobby;
